// FILE: app/(main)/exams/[slug]/ExamPageWrapper.tsx
"use client";

import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import ExamDetailClient from "./ExamDetailClient";
import { getExamPurchaseStatus } from "@/actions/exams";
import { getExamDraft } from "@/actions/exam-drafts";

interface CategoryContext {
  domain:   { name: string; slug: string } | null;
  org:      { name: string; slug: string } | null;
  position: { name: string; slug: string } | null;
}

interface Props {
  slug: string;
  exam: any;
  sections: { name: string; count: number }[];
  creator: any;
  sameAuthorExams: any[];
  categoryContext?: CategoryContext;
}

/**
 * Nội dung PUBLIC (exam, sections, creator, sameAuthorExams) đã được
 * server fetch sẵn ở page.tsx → render ngay, tốt cho SEO.
 *
 * Chỉ phần phụ thuộc AUTH (đã mua chưa, bài làm dở) mới fetch client-side.
 * Trong lúc chờ → vẫn render đầy đủ nội dung ở trạng thái "chưa mua" (guest),
 * KHÔNG còn spinner che cả trang như bản cũ.
 */
export default function ExamPageWrapper({
  slug,
  exam,
  sections,
  creator,
  sameAuthorExams,
  categoryContext,
}: Props) {
  const isFree = exam.price === 0 || exam.is_free;

  // Trạng thái auth — fetch sau, không chặn render nội dung
  const [authState, setAuthState] = useState<{
    user: any;
    isPurchased: boolean;
    isOwner: boolean;
    draft: { current_index: number; answers: any[]; time_spent: number } | null;
    ready: boolean;
  }>({
    user: null,
    isPurchased: isFree, // đề free coi như đã mở khoá
    isOwner: false,
    draft: null,
    ready: false,
  });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const purchaseStatus = await getExamPurchaseStatus(exam.id);
        // Chỉ fetch draft khi user đã đăng nhập (tránh query thừa cho guest)
        const draft = purchaseStatus.user ? await getExamDraft(exam.id) : null;

        if (cancelled) return;
        setAuthState({
          user: purchaseStatus.user,
          isPurchased: purchaseStatus.isPurchased || isFree,
          isOwner: purchaseStatus.isOwner ?? false,
          draft: draft ?? null,
          ready: true,
        });
      } catch (e) {
        console.error("[ExamPageWrapper] auth fetch error:", e);
        if (!cancelled) setAuthState((s) => ({ ...s, ready: true }));
      }
    })();

    return () => { cancelled = true; };
  }, [exam.id, isFree]);

  return (
    <ExamDetailClient
      exam={exam}
      sections={sections}
      creator={creator}
      sameAuthorExams={sameAuthorExams}
      categoryContext={categoryContext}
      user={authState.user}
      isPurchased={authState.isPurchased}
      isOwner={authState.isOwner}
      draft={authState.draft}
      authReady={authState.ready}
    />
  );
}
